import { Paginatable, PaginatableMeta, PaginatablePayload, SortableMeta, SortablePayload } from 'interfaces/table'
import { Collection } from 'interfaces'
import { CategoryId, TypedMoneyflowCategory } from 'interfaces/meta'
import { InternalPageProps } from 'interfaces/pages'
import { Timeframe } from 'interfaces/chart'
import { AggregatedBalanceByCategory } from 'interfaces/accounts'
import { Trend } from 'interfaces/trend'
import { TrendRankMode } from 'interfaces/rank'
import { WithRequestDetails } from 'interfaces/ajax/base'
import { TransactionRecatFlags } from 'interfaces/form'

export type MoneyflowType = 'spending' | 'income'
export type FullMoneyflowType = MoneyflowType | 'transactions'

export type MoneyflowTyped = {
  type: MoneyflowType
}

export type FullMoneyflowTyped = {
  type: FullMoneyflowType
}

export type MoneyflowPageStateProps = InternalPageProps & {
  categoryId: CategoryId
}

export type MoneyflowPageDispatchProps = {}

export type MoneyflowPageProps =
  & MoneyflowPageStateProps
  & MoneyflowPageDispatchProps

// ======================== State ======================== //
export type MoneyflowDataState = {
  breakdown: MoneyflowBreakdownData
  trends: MoneyflowMemberTrendData
  trackers: MoneyflowTrackersCollection | null
  transactions: Paginatable<Transaction, 'uid'> | null
}

export type MoneyflowAppState = {
  transactions: TransactionsAppState
  timeframe: Timeframe
  categories: MoneyflowCategoriesAppState
  breakdown: BreakdownAppState
  trackers: TrackersAppState
  trendMode: TrendRankMode
}

// ======================== Breakdown ======================== //

export type BreakdownAppState = {
  isExpanded: boolean
}

export type MoneyflowBreakdownData = Record<Timeframe, MoneyflowBreakdownCollection>

export type MoneyflowBreakdownItem = AggregatedBalanceByCategory & {
  average: number
  projected?: number
}

export type MoneyflowBreakdownCollection = Collection<MoneyflowBreakdownItem, 'category'>

// ========================== Trend ========================== //

export type MoneyflowMemberTrendsByCategory = Partial<Record<CategoryId, Trend>>

export type MoneyflowMemberTrendData = Record<Timeframe, MoneyflowMemberTrendsByCategory>

export type MoneyflowTrendOwnProps = {
  type: MoneyflowType
  timeframe: Timeframe
  categoryId: CategoryId
}

export type MoneyflowBreakdownOwnProps = {
  type: MoneyflowType
  timeframe: Timeframe
}

// ======================== Transactions ======================== //

type TransactionAccount = {
  id: number
  name: string
}

export type TransactionStatus = 'posted' | 'pending' | 'scheduled'

export type TransactionUid = string

type TransactionGeneric<ExtraFields> =
  & ExtraFields
  & {
    uid: TransactionUid
    amount: number
    date: string
    description: string
    status: TransactionStatus
  }

export const YODLEE_TRANSACTION_TYPE = 'yodlee' as const
export const RAILSBANK_TRANSACTION_TYPE = 'railsbank' as const

type YodleeTransaction = TransactionGeneric<{
  account_source_type: typeof YODLEE_TRANSACTION_TYPE
  account: TransactionAccount
  description_verbose: string | null
}>
type RailsbankTransaction = TransactionGeneric<{
  account_source_type: typeof RAILSBANK_TRANSACTION_TYPE
}>

export type BaseTransaction =
  | YodleeTransaction
  | RailsbankTransaction


export type RawTransaction = BaseTransaction & {
  category: CategoryId
}

export type RawRailsbankTransaction =
  & RailsbankTransaction
  & {
    category: CategoryId
  }

export type EditableTransactionKeys = 'amount' | 'date' | 'description' | 'category'

export type Transaction = BaseTransaction & {
  category: TypedMoneyflowCategory
}

export type PatchTransactionAPIPayload = {
  transaction: Partial<Pick<RawTransaction, EditableTransactionKeys>>
  apply_to_ids: TransactionUid[]
  create_rule: boolean
}

export type PatchTransactionsAPIPayload =
  & PatchTransactionAPIPayload
  & TransactionRecatFlags
  & {
    apply_to_ids: TransactionUid[]
  }

export type TransactionsAppState =
  & PaginatableMeta
  & SortableMeta
  & {
    isLoading: boolean
    editingId: TransactionUid | null
  }

export type BaseTransactionsPayload =
  & PaginatablePayload
  & SortablePayload
  & {
    type: FullMoneyflowType
    category?: CategoryId
    timeframe?: Timeframe
  }

export type TransactionsPayload = BaseTransactionsPayload & { transactions: Paginatable<Transaction, 'uid'> }

export type PatchTransactionResponse = {
  transaction: RawTransaction
}

export type RecentTransactionsItemsState = WithRequestDetails<TransactionUid[] | null>

export type RecentTransactionsDataState = {
  recent: RecentTransactionsItemsState
  all: Collection<Transaction, 'uid'>
}

// =============================== Trackers =============================== //

export type MoneyflowTrackersCollection = Collection<MoneyflowTracker, 'category'>

export type BaseMoneyflowTracker = {
  id: number
  limit: number
}

export type RawMoneyflowTracker = BaseMoneyflowTracker & {
  category: CategoryId | null
}

export type MoneyflowTracker = BaseMoneyflowTracker & {
  category: CategoryId
}

export type ExtendedMoneyflowTracker = MoneyflowTracker & {
  current: number
  projection: number
  severity: MoneyflowTrackerSeverity
  type: MoneyflowType
}

export type TrackersAppState = {
  expandedCategoryId: CategoryId | null
}

export type MoneyflowTrackerSeverity = 'danger' | 'warning' | 'on-track'

// =============================== Categories ============================== //

export type MoneyflowCategoriesAppState = {
  selectedId: CategoryId
  highlightedId: CategoryId
  isExpanded: boolean
}
